import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, SpecraBadge, SpecraButton, SpecraPageHeader, SpecraTooltip, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTablePreferences } from '../../../hooks/useTablePreferences';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { fromSelectValue, labelForOption, toSelectValue, withNoneOption, withStoredFallback } from '../_shared/api/selectOptions';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { ContactPicker } from '../_shared/components/ContactPicker';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { EmptyState } from '../_shared/components/PageStates';
import { RolePicker } from '../_shared/components/RolePicker';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { UrgencyDate } from '../_shared/components/UrgencyDate';
import { POTENTIAL_ISSUES_COPY } from '../_shared/constants/sectionCopy';
import { useContractCLINs } from '../Programs/contractHooks';
import { useProgramWriteAccess } from '../Programs/programWriteAccess';
import { occurrenceLinkFor, riskHooks, useConvertRiskToIssue, useDeliverableLinkOptions, useEscalateRiskToLeadership } from './hooks';
import { RISK_IMPACT, RISK_PROBABILITY, RISK_STATUS, RISK_STRATEGY } from './options';
const ymd = (v)=>v ? formatLocalYmd(v) ?? '—' : '—';
// "Nov 3, 2024" for the resolved-date badge on closed rows (the table column
// prints the human month; the drawer keeps the exact YYYY-MM-DD).
const prettyDate = (v)=>{
    const s = formatLocalYmd(v);
    if (!s) return '—';
    return parseLocalDate(s).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};
// A closed risk keeps its target date for the record, but no longer carries
// urgency — the deadline is history, not live exposure.
const isRiskClosed = (r)=>(r.status ?? '').toLowerCase() === 'closed';
// "Converted" is server-set the moment Convert to Issue succeeds — the
// affordance is spent and must not show again on that row.
const isConverted = (r)=>(r.status ?? '').toLowerCase() === 'converted';
// Mirrors isTopBand / isCriticalPotentialIssue in
// services/pangaea/pkg/connecthandlers/delivery_risk.go verbatim — the
// Escalate to leadership action must not invent a second criticality rule.
// Anchored, case-insensitive, trimmed match against the Low/Medium/High
// vocabulary's top band plus the two legacy free-text spellings.
const isTopBand = (s)=>!!s && [
        'high',
        'critical',
        'very high'
    ].includes(s.trim().toLowerCase());
const isCriticalPotentialIssue = (probability, impact)=>isTopBand(probability) && isTopBand(impact);
// The leadership-escalation snapshot (migration 000113) rides on the risk
// projection itself, so "already escalated at these bands" is derived
// straight from the record — no second fetch. A probability/impact edit
// since the last escalation makes the live bands differ from the stored
// snapshot, which re-arms the action, mirroring EscalateRiskToLeadership's
// guard in delivery_risk.go.
const isEscalatedAtCurrentBands = (r)=>!!r.leadershipEscalatedAt && r.leadershipEscalatedProbability === r.probability && r.leadershipEscalatedImpact === r.impact;
// Status → semantic tone. Open is live exposure (warning); Mitigating is an
// in-flight response (info); Closed is resolved (success); Converted is
// server-set (info) once Convert to Issue succeeds.
const RISK_STATUS_TONE = {
    Open: 'warning',
    Mitigating: 'info',
    Closed: 'success',
    Converted: 'info'
};
// "Converted" is server-set and deliberately not in RISK_STATUS (not a user-
// selectable option), so a converted record's Status select needs the stored
// value prepended or it blanks on open and silently re-statuses on save —
// mirrors IssuesTab's impactOptionsFor idiom.
const statusOptionsFor = (current)=>{
    if (current && !RISK_STATUS.some((o)=>o.value === current)) {
        return [
            {
                value: current,
                label: current
            },
            ...RISK_STATUS
        ];
    }
    return RISK_STATUS;
};
// Probability / impact → severity tone. Critical is exposure (danger), High is
// orange, Medium is watch (warning), Low is background (neutral).
const LEVEL_TONE = {
    High: 'orange',
    Critical: 'danger',
    Medium: 'warning',
    Low: 'neutral'
};
const LevelBadge = ({ value })=>value ? /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: LEVEL_TONE[value] ?? 'neutral',
        children: value
    }) : /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
// Two-letter monogram for the owner column, mirroring the mockup's inline avatar.
const initials = (name)=>name.split(/\s+/).filter(Boolean).slice(0, 2).map((w)=>w[0]?.toUpperCase() ?? '').join('');
function OwnerCell({ name }) {
    if (!name) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2 whitespace-nowrap",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[0.5rem] font-medium leading-none text-muted-foreground",
                children: initials(name)
            }),
            /*#__PURE__*/ _jsx("span", {
                children: name
            })
        ]
    });
}
// Front columns are the at-a-glance triage fields; the overflow (audience,
// escalation, comments, links, resolution dates) lives in the read-only detail
// drawer opened by clicking a row.
// Sortable, clean headers with hairline borders and rectangular badges — no
// always-visible filter row (the mockup reads as a plain register; sorting is
// the only column affordance). Filtering was dropped here to match it.
const columns = [
    {
        key: 'shortDescription',
        header: 'Potential Issue',
        sortable: true
    },
    {
        key: 'category',
        header: 'Category',
        sortable: true,
        render: (r)=>/*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: r.category || '—'
            })
    },
    {
        key: 'probability',
        header: 'Prob',
        sortable: true,
        render: (r)=>/*#__PURE__*/ _jsx(LevelBadge, {
                value: r.probability
            })
    },
    {
        key: 'impact',
        header: 'Impact',
        sortable: true,
        render: (r)=>/*#__PURE__*/ _jsx(LevelBadge, {
                value: r.impact
            })
    },
    {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: RISK_STATUS_TONE[r.status] ?? 'neutral',
                children: r.status || '—'
            })
    },
    {
        key: 'owner',
        header: 'Owner',
        render: (r)=>/*#__PURE__*/ _jsx(OwnerCell, {
                name: r.owner
            })
    },
    {
        key: 'strategy',
        header: 'Strategy',
        sortable: true,
        render: (r)=>/*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: r.strategy || '—'
            })
    },
    {
        key: 'targetResolutionDate',
        header: 'Target',
        align: 'right',
        sortable: true,
        sortAccessor: (r)=>Number(r.targetResolutionDate?.seconds ?? 0),
        // A closed risk shows its resolution date for the record ("Resolved Nov 3,
        // 2024"), muted — history, not live exposure. Open risks carry urgency.
        render: (r)=>isRiskClosed(r) ? /*#__PURE__*/ _jsx("span", {
                className: "whitespace-nowrap text-muted-foreground",
                children: r.actualResolutionDate ? `Resolved ${prettyDate(r.actualResolutionDate)}` : '—'
            }) : /*#__PURE__*/ _jsx(UrgencyDate, {
                value: r.targetResolutionDate
            })
    }
];
// CLIN / deliverable options are dynamic (the program's CLINs and its
// contract-model deliverables),
// so the field list is built per-render.
function fieldsFor(clins, deliverables, statusOptions) {
    return [
        {
            kind: 'text',
            name: 'shortDescription',
            label: 'Short description',
            required: true,
            maxLength: 300
        },
        {
            kind: 'text',
            name: 'category',
            label: 'Category',
            maxLength: 100
        },
        {
            kind: 'select',
            name: 'probability',
            label: 'Probability',
            options: RISK_PROBABILITY,
            helpText: POTENTIAL_ISSUES_COPY.probabilityHelp
        },
        {
            kind: 'select',
            name: 'impact',
            label: 'Impact',
            options: RISK_IMPACT,
            helpText: POTENTIAL_ISSUES_COPY.impactHelp
        },
        {
            kind: 'select',
            name: 'status',
            label: 'Status',
            options: statusOptions
        },
        {
            kind: 'select',
            name: 'strategy',
            label: 'Strategy',
            options: RISK_STRATEGY
        },
        {
            kind: 'textarea',
            name: 'strategyPlan',
            label: 'Strategy plan',
            rows: 3
        },
        // Owner, Audience, and Escalation are NOT RHF fields — they're pickers in extraContent.
        {
            kind: 'select',
            name: 'linkedClinId',
            label: 'Linked CLIN',
            options: clins
        },
        {
            kind: 'select',
            name: 'linkedDutyId',
            label: 'Linked deliverable',
            options: deliverables,
            testId: Testids.cmRiskLinks.riskDeliverable
        },
        {
            kind: 'textarea',
            name: 'comments',
            label: 'Comments',
            rows: 2
        },
        {
            kind: 'date',
            name: 'targetResolutionDate',
            label: 'Target resolution date'
        }
    ];
}
export function RisksTab({ programId }) {
    const canEdit = useProgramWriteAccess(programId).riskIssues;
    const { data, isLoading } = riskHooks.useList({
        programId
    });
    const upsert = riskHooks.useUpsert();
    const remove = riskHooks.useDelete();
    const convert = useConvertRiskToIssue();
    const escalate = useEscalateRiskToLeadership();
    const prefs = useTablePreferences('ts:delivery:risks');
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [escalating, setEscalating] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Owner is parent-controlled (single source of truth): the RolePicker fills
    // the contact id + display name. Neither touches the RHF form, so a pick never
    // resets the other fields. The name rides along because the home strip prints
    // the free-text owner column.
    const [ownerContactId, setOwnerContactId] = useState('');
    const [ownerName, setOwnerName] = useState('');
    // Audience is a Contacts PoC, mirroring owner: the picker fills the name + id.
    const [audienceContactId, setAudienceContactId] = useState('');
    const [audienceName, setAudienceName] = useState('');
    // Escalation is a person (internal role), mirroring owner. The picker's contact
    // id is session-only (the schema keeps just the name in escalation_path); on
    // edit the stored name rides in via the picker's fallback label.
    const [escalationContactId, setEscalationContactId] = useState('');
    const [escalationName, setEscalationName] = useState('');
    // Dynamic dropdown sources: CLINs + deliverables (links).
    const clins = useContractCLINs(programId).data?.clins ?? [];
    const clinOptions = withNoneOption(clins.map((c)=>({
            value: c.id,
            label: `${c.clinNumber} ${c.shortTitle}`.trim()
        })));
    const deliverableOptions = useDeliverableLinkOptions(programId);
    // Shared by the create modal and drawer edit-in-place; the target id is
    // passed in (empty = create) since the two surfaces track different records
    // (modal → `editing`, drawer → `detail`).
    const submitRisk = async (values, id)=>{
        const stored = id ? items.find((r)=>r.id === id) : undefined;
        const linkedDutyId = fromSelectValue(values.linkedDutyId ?? '');
        await upsert.mutateAsync({
            id,
            programId,
            category: values.category ?? '',
            audience: audienceName,
            status: values.status ?? 'Open',
            strategy: values.strategy ?? '',
            probability: values.probability ?? '',
            impact: values.impact ?? '',
            shortDescription: values.shortDescription,
            strategyPlan: values.strategyPlan ?? '',
            // Owner and Audience come from parent state, not RHF — each picker fills
            // the display name + contact id.
            owner: ownerName,
            ownerContactId,
            audienceContactId,
            // Escalation stores the picked person's name (the schema has no contact id
            // column for it), mirroring how owner keeps its display name in sync.
            escalationPath: escalationName,
            linkedClinId: fromSelectValue(values.linkedClinId ?? ''),
            // The old model's link goes back exactly as stored (a plain string on
            // the wire, so dropping it would clear it).
            linkedDeliverableId: stored?.linkedDeliverableId ?? '',
            linkedDutyId,
            linkedOccurrenceId: occurrenceLinkFor(stored?.linkedDutyId, linkedDutyId),
            comments: values.comments ?? '',
            targetResolutionDate: values.targetResolutionDate ? Timestamp.fromDate(parseLocalDate(values.targetResolutionDate)) : undefined
        });
    };
    // Seed the out-of-form pickers (owner / audience / escalation) from a record.
    const seedPickersFrom = (r)=>{
        setOwnerContactId(r?.ownerContactId ?? '');
        setOwnerName(r?.owner ?? '');
        setAudienceContactId(r?.audienceContactId ?? '');
        setAudienceName(r?.audience ?? '');
        // No stored contact id for escalation — seed the name only; the picker
        // surfaces it via its fallback label.
        setEscalationContactId('');
        setEscalationName(r?.escalationPath ?? '');
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    const handleEscalateConfirm = async ()=>{
        if (!escalating) return;
        try {
            await escalate.mutateAsync(escalating.id);
        } catch  {
        // onError above already surfaces the toast; swallow here so a failed
        // escalation doesn't become an unhandled promise rejection.
        } finally{
            setEscalating(null);
        }
    };
    const openCreate = ()=>{
        setEditing(null);
        seedPickersFrom(null);
        setFormOpen(true);
    };
    // The owner/audience/escalation pickers — shared verbatim by the create modal
    // (stacked in pickerContent) and the drawer's inline edit (each dropped into
    // its Ownership row via editControl). All three drive the same parent state.
    const ownerPicker = /*#__PURE__*/ _jsx(RolePicker, {
        id: "ownerContactId",
        label: "Owner",
        helpText: "Pick from this program's internal roles. Someone not listed must be added under Roles first.",
        programId: programId,
        value: ownerContactId,
        fallbackLabel: ownerName,
        onClear: ()=>{
            setOwnerContactId('');
            setOwnerName('');
        },
        onChange: (cid, name)=>{
            setOwnerContactId(cid);
            setOwnerName(name);
        }
    });
    const audiencePicker = /*#__PURE__*/ _jsx(ContactPicker, {
        id: "audienceContactId",
        label: "Audience",
        helpText: POTENTIAL_ISSUES_COPY.audienceHelp,
        programId: programId,
        value: audienceContactId,
        testid: Testids.deliveryRisk.audiencePicker,
        fallbackLabel: audienceName,
        onClear: ()=>{
            setAudienceContactId('');
            setAudienceName('');
        },
        onChange: (cid, name)=>{
            setAudienceContactId(cid);
            setAudienceName(name);
        }
    });
    const escalationPicker = /*#__PURE__*/ _jsx(RolePicker, {
        id: "escalationContactId",
        label: "Escalation path",
        helpText: "Who this risk escalates to — pick from this program's internal roles.",
        programId: programId,
        value: escalationContactId,
        testid: Testids.deliveryRisk.escalationPicker,
        fallbackLabel: escalationName,
        onClear: ()=>{
            setEscalationContactId('');
            setEscalationName('');
        },
        onChange: (cid, name)=>{
            setEscalationContactId(cid);
            setEscalationName(name);
        }
    });
    const pickerContent = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            ownerPicker,
            audiencePicker,
            escalationPicker
        ]
    });
    // Build the risk form config (fields + defaults) for a given record — reused
    // by the create modal (editing) and the drawer edit (detail).
    const riskFormConfig = (r)=>({
            fields: fieldsFor(withStoredFallback(clinOptions, r?.linkedClinId ?? ''), withStoredFallback(deliverableOptions, r?.linkedDutyId ?? ''), statusOptionsFor(r?.status ?? '')),
            defaultValues: {
                id: r?.id ?? '',
                shortDescription: r?.shortDescription ?? '',
                category: r?.category ?? '',
                probability: r?.probability ?? '',
                impact: r?.impact ?? '',
                status: r?.status ?? 'Open',
                strategy: r?.strategy ?? '',
                strategyPlan: r?.strategyPlan ?? '',
                linkedClinId: toSelectValue(r?.linkedClinId ?? ''),
                linkedDutyId: toSelectValue(r?.linkedDutyId ?? ''),
                comments: r?.comments ?? '',
                targetResolutionDate: formatLocalYmd(r?.targetResolutionDate) ?? ''
            }
        });
    // The drawer's inline-edit config, bound to the record being viewed. The
    // editable controls live on the rows (below); this carries only the form-level
    // concerns.
    const detailEdit = detail ? {
        defaultValues: riskFormConfig(detail).defaultValues,
        onSubmit: (v)=>submitRisk(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>seedPickersFrom(detail)
    } : undefined;
    // Detail sections: read values plus, per row, the inline-edit control. Standard
    // fields carry an `editField` (bound to the drawer's form); owner/audience/
    // escalation carry an `editControl` (the parent-controlled pickers). Rows with
    // neither (Resolved) stay read-only even in edit mode. The link selects reuse
    // the same stored-fallback option lists the modal uses, so a superseded CLIN
    // still shows as "unavailable" instead of blanking.
    const detailClinOptions = withStoredFallback(clinOptions, detail?.linkedClinId ?? '');
    const detailDeliverableOptions = withStoredFallback(deliverableOptions, detail?.linkedDutyId ?? '');
    const detailSections = detail ? [
        {
            heading: 'Assessment',
            rows: [
                {
                    label: 'Short description',
                    value: detail.shortDescription || '—',
                    editField: {
                        kind: 'text',
                        name: 'shortDescription',
                        label: 'Short description',
                        required: true,
                        maxLength: 300
                    }
                },
                {
                    label: 'Category',
                    value: detail.category || '—',
                    editField: {
                        kind: 'text',
                        name: 'category',
                        label: 'Category',
                        maxLength: 100
                    }
                },
                {
                    label: 'Probability',
                    value: detail.probability || '—',
                    editField: {
                        kind: 'select',
                        name: 'probability',
                        label: 'Probability',
                        options: RISK_PROBABILITY,
                        helpText: POTENTIAL_ISSUES_COPY.probabilityHelp
                    }
                },
                {
                    label: 'Impact',
                    value: detail.impact || '—',
                    editField: {
                        kind: 'select',
                        name: 'impact',
                        label: 'Impact',
                        options: RISK_IMPACT,
                        helpText: POTENTIAL_ISSUES_COPY.impactHelp
                    }
                },
                // Converted is server-owned and terminal: once Convert to Issue
                // succeeds, the editor must not offer a way back to a live status
                // (that would re-open exposure the linked Issue no longer tracks).
                // defaultValues still carries the stored status (see
                // riskFormConfig) so the omitted editField never drops it from the
                // upsert's full-replace payload.
                isConverted(detail) ? {
                    label: 'Status',
                    value: detail.status || '—'
                } : {
                    label: 'Status',
                    value: detail.status || '—',
                    editField: {
                        kind: 'select',
                        name: 'status',
                        label: 'Status',
                        options: statusOptionsFor(detail.status)
                    }
                },
                {
                    label: 'Strategy',
                    value: detail.strategy || '—',
                    editField: {
                        kind: 'select',
                        name: 'strategy',
                        label: 'Strategy',
                        options: RISK_STRATEGY
                    }
                }
            ]
        },
        {
            heading: 'Ownership',
            rows: [
                {
                    label: 'Owner',
                    value: detail.owner || '—',
                    editControl: ownerPicker
                },
                {
                    label: 'Audience',
                    value: detail.audience || '—',
                    editControl: audiencePicker
                },
                {
                    label: 'Escalation path',
                    value: detail.escalationPath || '—',
                    editControl: escalationPicker
                }
            ]
        },
        {
            heading: 'Links',
            rows: [
                {
                    label: 'Linked CLIN',
                    value: labelForOption(detail.linkedClinId, clinOptions),
                    editField: {
                        kind: 'select',
                        name: 'linkedClinId',
                        label: 'Linked CLIN',
                        options: detailClinOptions
                    }
                },
                {
                    label: 'Linked deliverable',
                    value: labelForOption(detail.linkedDutyId, deliverableOptions),
                    editField: {
                        kind: 'select',
                        name: 'linkedDutyId',
                        label: 'Linked deliverable',
                        options: detailDeliverableOptions,
                        testId: Testids.cmRiskLinks.riskDeliverable
                    }
                }
            ]
        },
        {
            heading: 'Plan',
            rows: [
                {
                    label: 'Strategy plan',
                    value: detail.strategyPlan || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'strategyPlan',
                        label: 'Strategy plan',
                        rows: 3
                    }
                },
                {
                    label: 'Comments',
                    value: detail.comments || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'comments',
                        label: 'Comments',
                        rows: 2
                    }
                }
            ]
        },
        {
            heading: 'Dates',
            rows: [
                {
                    label: 'Target resolution',
                    value: ymd(detail.targetResolutionDate),
                    editField: {
                        kind: 'date',
                        name: 'targetResolutionDate',
                        label: 'Target resolution date'
                    }
                },
                // Server-stamped when a risk closes — no edit control (read-only).
                {
                    label: 'Resolved',
                    value: ymd(detail.actualResolutionDate)
                }
            ]
        }
    ] : [];
    const newButton = /*#__PURE__*/ _jsx(SpecraButton, {
        onClick: openCreate,
        disabled: !canEdit,
        "data-testid": Testids.deliveryRisk.riskCreate,
        children: "New Potential Issue"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        POTENTIAL_ISSUES_COPY.label,
                        ' ',
                        /*#__PURE__*/ _jsxs("span", {
                            className: "font-normal text-muted-foreground",
                            children: [
                                "· ",
                                items.length
                            ]
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: POTENTIAL_ISSUES_COPY.sectionInfo,
                            children: /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "aria-label": "About Potential Issues",
                                "data-testid": Testids.deliveryRisk.sectionInfo,
                                className: "inline-flex align-middle text-muted-foreground hover:text-foreground",
                                children: /*#__PURE__*/ _jsx(Info, {
                                    className: "h-4 w-4",
                                    "aria-hidden": true
                                })
                            })
                        })
                    ]
                }),
                description: "Owners, strategies, and target resolutions — closed risks keep their resolution date for the record.",
                actions: newButton
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryRisk.risksTable,
                rowTestid: Testids.deliveryRisk.riskRow,
                rowKey: (r)=>r.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                sort: prefs.sort,
                onToggleSort: prefs.toggleSort,
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: POTENTIAL_ISSUES_COPY.emptyTitle,
                    description: POTENTIAL_ISSUES_COPY.emptyDescription,
                    action: !canEdit ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: openCreate,
                        children: "New Potential Issue"
                    })
                }),
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            canEdit && !isConverted(row) && /*#__PURE__*/ _jsx(SpecraTooltip, {
                                content: POTENTIAL_ISSUES_COPY.convertTooltip,
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    disabled: convert.isPending,
                                    onClick: ()=>convert.mutate(row.id),
                                    "data-testid": Testids.deliveryRisk.riskConvert,
                                    children: POTENTIAL_ISSUES_COPY.convertLabel
                                })
                            }),
                            canEdit && isCriticalPotentialIssue(row.probability, row.impact) && (isEscalatedAtCurrentBands(row) ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                                content: POTENTIAL_ISSUES_COPY.escalateAlreadyEscalatedTooltip,
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    "aria-disabled": true,
                                    className: "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
                                    onClick: ()=>undefined,
                                    "data-testid": Testids.deliveryRisk.riskEscalate,
                                    children: POTENTIAL_ISSUES_COPY.escalateLabel
                                })
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setEscalating(row),
                                "data-testid": Testids.deliveryRisk.riskEscalate,
                                children: POTENTIAL_ISSUES_COPY.escalateLabel
                            })),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('read');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryRisk.riskDetails,
                                children: "Details"
                            }),
                            canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        // Edit-in-place: open the drawer straight into edit mode; the
                                        // drawer's onEditStart seeds the pickers from this record.
                                        onClick: ()=>{
                                            setDetailMode('edit');
                                            setDetailId(row.id);
                                        },
                                        "data-testid": Testids.deliveryRisk.riskEdit,
                                        children: "Edit"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                                        onClick: ()=>setDeleting(row),
                                        "data-testid": Testids.deliveryRisk.riskDelete,
                                        children: "Delete"
                                    })
                                ]
                            })
                        ]
                    })
            }),
            items.length > 0 && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Click a risk for its strategy plan · closed risks keep their resolution date for the record."
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New Potential Issue",
                ...riskFormConfig(editing),
                extraContent: pickerContent,
                onSubmit: async (v)=>{
                    await submitRisk(v, editing?.id ?? '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Potential Issue",
                description: deleting ? `Delete potential issue "${deleting.shortDescription}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: !!escalating,
                onOpenChange: (open)=>!open && setEscalating(null),
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: POTENTIAL_ISSUES_COPY.escalateConfirmTitle
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogDescription, {
                                    children: POTENTIAL_ISSUES_COPY.escalateConfirmDescription
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    className: specraButtonVariants({
                                        variant: 'secondary'
                                    }),
                                    disabled: escalate.isPending,
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    className: specraButtonVariants({
                                        variant: 'default'
                                    }),
                                    disabled: escalate.isPending,
                                    onClick: ()=>void handleEscalateConfirm(),
                                    "data-testid": Testids.deliveryRisk.riskEscalateConfirm,
                                    children: escalate.isPending ? 'Escalating…' : 'Escalate'
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail?.shortDescription || 'Potential Issue',
                sections: detailSections,
                testid: Testids.deliveryRisk.riskDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode
            })
        ]
    });
}
